import { useMemo, useState } from 'react'
import { memo } from 'react'

import {
  Anchor,
  Badge,
  Button,
  Group,
  LoadingOverlay,
  Select,
  Stack,
  Text,
} from '@mantine/core'
import { DateInput } from '@mantine/dates'
import { useDebouncedValue } from '@mantine/hooks'
import { IconFileSearch, IconSearch } from '@tabler/icons-react'
import { useNavigate } from '@tanstack/react-router'
import dayjs from 'dayjs'
import { upperCase } from 'lodash-es'

import {
  type AuditLog as AuditLogEntry,
  useAuditLogs,
} from '@repo/common/queries/admin/audit'

import { TowerCard } from '@/components/ui/TowerCard'
import {
  column,
  mapColumn,
  TowerDataTable,
  type TowerDataTableColumn,
} from '@/components/ui/TowerDataTable'

export type AuditLogProps = Record<string, never>

const severityOptions = [
  { value: '', label: 'All Severities' },
  { value: 'info', label: 'Info' },
  { value: 'warning', label: 'Warning' },
  { value: 'error', label: 'Error' },
  { value: 'critical', label: 'Critical' },
]

const AuditLog = memo<AuditLogProps>(function AuditLogPage() {
  const [userFilter, setUserFilter] = useState('')
  const [severityFilter, setSeverityFilter] = useState('')
  const [startDate, setStartDate] = useState<string | null>(null)
  const [endDate, setEndDate] = useState<string | null>(null)
  const [debouncedUserFilter] = useDebouncedValue(userFilter, 1000)
  const [limit] = useState(100)
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())
  const navigate = useNavigate()

  const { data: auditLogs, isLoading: loading } = useAuditLogs({
    user_id: debouncedUserFilter || undefined,
    severity: severityFilter || undefined,
    start_date: startDate == null ? undefined : dayjs(startDate).toISOString(),
    end_date: endDate == null ? undefined : dayjs(endDate).toISOString(),
    limit,
  })

  const logs = useMemo<AuditLogEntry[]>(() => {
    return auditLogs || []
  }, [auditLogs])

  const getSeverityColor = (severity: string) => {
    switch (severity.toLowerCase()) {
      case 'info':
        return 'blue'
      case 'warning':
        return 'yellow'
      case 'error':
      case 'critical':
        return 'red'
      default:
        return 'gray'
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
  }

  const clearFilters = () => {
    setUserFilter('')
    setSeverityFilter('')
    setStartDate(null)
    setEndDate(null)
  }

  const renderReference = (reference: string) => {
    let navExternal: string | undefined
    let navInternal = undefined
    let title = ''

    if (reference.startsWith('vault:')) {
      const vaultId = reference.replace('vault:', '')
      navInternal = { to: '/vault/$id', params: { id: vaultId } }
      title = `${vaultId} Vault`

      // return (
      //   <Anchor
      //     size="sm"
      //     onClick={() =>
      //       void navigate({ to: '/vault/$id', params: { id: vaultId } })
      //     }
      //     style={{ cursor: 'pointer' }}
      //   >
      //     {vaultId} Vault
      //   </Anchor>
      // )
    }

    if (reference.startsWith('tx_hash:hardhat:')) {
      const txHash = reference.replace('tx_hash:hardhat:', '')
      navExternal = `https://app.tryethernal.com/transaction/${txHash}`
      title = `Hardhat Tx: ${txHash.slice(0, 8)}...`

      // return (
      //   <Anchor
      //     size="sm"
      //     href={`https://app.tryethernal.com/transaction/${txHash}`}
      //     target="_blank"
      //     rel="noopener noreferrer"
      //   >
      //     {txHash.slice(0, 8)}...
      //   </Anchor>
      // )
    }

    if (reference.startsWith('tx_hash:sepolia:')) {
      const txHash = reference.replace('tx_hash:sepolia:', '')
      navExternal = `https://sepolia.etherscan.io/tx/${txHash}`
      title = `${txHash.slice(0, 8)}...`

      // return (
      //   <Anchor
      //     size="sm"
      //     href={`https://sepolia.etherscan.io/tx/${txHash}`}
      //     target="_blank"
      //     rel="noopener noreferrer"
      //   >
      //     {txHash.slice(0, 8)}...
      //   </Anchor>
      // )
    }

    if (reference.startsWith('tx_hash:mainnet:')) {
      const txHash = reference.replace('tx_hash:mainnet:', '')
      navExternal = `https://etherscan.io/tx/${txHash}`
      title = `${txHash.slice(0, 8)}...`
      //
      // return (
      //   <Anchor
      //     size="sm"
      //     href={`https://etherscan.io/tx/${txHash}`}
      //     target="_blank"
      //     rel="noopener noreferrer"
      //   >
      //     {txHash.slice(0, 8)}...
      //   </Anchor>
      // )
    }

    if (reference.startsWith('fund:')) {
      const fundId = reference.replace('fund:', '')
      navInternal = { to: '/fund/$id', params: { id: fundId } }
      title = `Fund ${fundId}`
      // return (
      //   <Anchor
      //     size="sm"
      //     onClick={() =>
      //       void navigate({ to: '/fund/$id', params: { id: fundId } })
      //     }
      //     style={{ cursor: 'pointer' }}
      //   >
      //     Fund {fundId}
      //   </Anchor>
      // )
    }

    if (navExternal) {
      return (
        <Anchor
          size="sm"
          href={navExternal}
          target="_blank"
          rel="noopener noreferrer"
          title={title}
          style={{ cursor: 'pointer' }}
        >
          {title}
        </Anchor>
      )
    }

    if (navInternal) {
      return (
        <Anchor
          size="sm"
          onClick={() => void navigate(navInternal)}
          style={{ cursor: 'pointer' }}
          title={title}
        >
          {title}
        </Anchor>
      )
    }

    return (
      <Text size="sm" c="dimmed">
        {reference.split(/[:-]+/).join(' ')}
      </Text>
    )
  }

  const columns: TowerDataTableColumn<AuditLogEntry>[] = [
    column({
      accessor: 'created_at',
      title: 'Date/Time',
      width: '15%',
      render: (date) => (
        <Text size="sm" fw={500}>
          {formatDate(date)}
        </Text>
      ),
    }),
    mapColumn({
      accessor: (log) => log.severity,
      title: 'Severity',
      width: '10%',
      render: (severity) => (
        <Badge color={getSeverityColor(severity)} variant="light">
          {severity.toUpperCase()}
        </Badge>
      ),
    }),
    mapColumn({
      accessor: (log) => [log.action, log.severity],
      title: 'Action',
      width: '10%',
      render: (as) => (
        <Text
          size="xs"
          fw={as[1] === 'critical' ? 800 : 500}
          c={getSeverityColor(as[1]!)}
        >
          {upperCase(as[0])}
        </Text>
      ),
    }),
    column({
      accessor: 'description',
      title: 'Description',
      render: (description) => <Text size="sm">{description}</Text>,
    }),
    mapColumn({
      accessor: (log) => log.user || log.user_id,
      title: 'User',
      width: '15%',
      render: (_, log) => {
        if (log.user) {
          return (
            <Group gap="xs">
              {/*<Avatar size="sm" radius="xl" name={log.user.name || log.user.email || 'Unknown'} />*/}
              <div>
                <Text size="sm" fw={500} lineClamp={1}>
                  {log.user.name || log.user.email || 'Unknown User'}
                </Text>
                <Text size="xs" c="dimmed" lineClamp={1}>
                  {log.user.roles?.join(', ') || 'No roles'}
                </Text>
              </div>
            </Group>
          )
        }
        return (
          <Text size="sm" c="dimmed">
            {log.user_id}
          </Text>
        )
      },
    }),
    // column({
    //   accessor: 'ip_address',
    //   title: 'IP Address',
    //   width: '12%',
    //   render: (ipAddress) => (
    //     <Text size="sm" c="dimmed">
    //       {ipAddress}
    //     </Text>
    //   ),
    // }),
    mapColumn({
      accessor: (log) => log.tags,
      title: 'Tags',
      width: '5%',
      render: (tags) => (
        <Group gap={4}>
          <Text size="xs" c="dimmed" style={{ textTransform: 'capitalize' }}>
            {tags.join(', ')}
          </Text>
        </Group>
      ),
    }),
    mapColumn({
      accessor: (log) => log.references,
      title: 'References',
      width: '15%',

      render: (references, log) => {
        const MAX_CHARS = 300
        const referencesText = references.join(', ')
        const rowKey = `${log.id || log.created_at}-references`
        const isExpanded = expandedRows.has(rowKey)
        const shouldTruncate = referencesText.length > MAX_CHARS
        const displayReferences =
          shouldTruncate && !isExpanded
            ? references.slice(
                0,
                Math.floor(
                  references.length * (MAX_CHARS / referencesText.length),
                ),
              )
            : references

        const toggleExpanded = () => {
          const newExpanded = new Set(expandedRows)
          if (isExpanded) {
            newExpanded.delete(rowKey)
          } else {
            newExpanded.add(rowKey)
          }
          setExpandedRows(newExpanded)
        }

        return (
          <Stack gap={2}>
            <div
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {displayReferences.map((reference, index) => (
                <div key={index} style={{ marginBottom: 2 }}>
                  {renderReference(reference)}
                </div>
              ))}
              {shouldTruncate && !isExpanded && (
                <Text size="xs" c="dimmed">
                  ... and {references.length - displayReferences.length} more
                </Text>
              )}
            </div>
            {shouldTruncate && (
              <Anchor
                size="xs"
                onClick={toggleExpanded}
                style={{ cursor: 'pointer' }}
              >
                {isExpanded ? 'Show less' : 'Show more'}
              </Anchor>
            )}
          </Stack>
        )
      },
    }),
  ]

  const filterControls = (
    <Group>
      <IconSearch size={16} />
      {/*<TextInput*/}
      {/*  placeholder="Filter by user..."*/}
      {/*  value={userFilter}*/}
      {/*  onChange={(event) => setUserFilter(event.currentTarget.value)}*/}
      {/*  size="sm"*/}
      {/*  style={{ width: 200 }}*/}
      {/*/>*/}
      <Select
        placeholder="Severity"
        data={severityOptions}
        value={severityFilter}
        onChange={(value) => {
          setSeverityFilter(value || '')
        }}
        size="sm"
        style={{ width: 150 }}
        clearable
      />
      <DateInput
        placeholder="Start date"
        value={startDate}
        onChange={setStartDate}
        valueFormat="YYYY-MM-DD"
        size="sm"
        style={{ width: 150 }}
        clearable
      />
      <DateInput
        placeholder="End date"
        value={endDate}
        onChange={setEndDate}
        valueFormat="YYYY-MM-DD"
        size="sm"
        style={{ width: 150 }}
        clearable
      />
      <Button variant="outline" size="sm" onClick={clearFilters}>
        Clear
      </Button>
    </Group>
  )

  return (
    <Stack>
      <TowerCard
        title="Audit Log"
        description="View system audit logs and security events"
        icon={<IconFileSearch size={20} />}
        extraHeader={filterControls}
      >
        <div style={{ position: 'relative' }}>
          <LoadingOverlay visible={loading} />

          <TowerDataTable
            data={logs}
            columns={columns}
            emptyMessage="No audit logs found"
            isLoading={loading}
            maxHeight={700}
          />
        </div>
      </TowerCard>
    </Stack>
  )
})

export default AuditLog
